import React, { useEffect, useMemo, lazy, Suspense } from 'react';
import Footer from '@/components/Footer';
import NavBar from '@/components/NavBar';
import SEOHead from '@/components/SEOHead';
import BreadcrumbSEO from '@/components/BreadcrumbSEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const AdSlot = lazy(() => import('@/components/AdSlot'));

const FAQ = () => {
  const faqItems = [
    {
      question: "¿Cómo funciona el compresor de imágenes?",
      answer: "Nuestro compresor analiza cada imagen y aplica algoritmos de compresión adaptativos directamente en tu navegador. El proceso es 100% local: tus imágenes nunca se envían a ningún servidor. En segundos obtienes una versión optimizada con el menor tamaño posible y la mayor calidad visual."
    },
    {
      question: "¿Qué formatos de imagen son compatibles?",
      answer: "Soportamos JPEG/JPG, PNG, WebP y GIF. Cada formato se procesa con el algoritmo más adecuado: compresión con pérdida para JPEG (ideal para fotos), sin pérdida para PNG (conserva transparencia), y optimización avanzada para WebP."
    },
    {
      question: "¿Hay límite de imágenes o tamaño de archivo?",
      answer: "No. CompresorJPEG es completamente gratuito y sin límites. Puedes comprimir tantas imágenes como necesites, del tamaño que quieras, sin registro ni suscripción."
    },
    {
      question: "¿Es seguro subir mis imágenes?",
      answer: "Totalmente seguro. Toda la compresión ocurre dentro de tu navegador usando tu propio procesador. Las imágenes nunca abandonan tu dispositivo ni se envían a ningún servidor. No almacenamos ningún archivo."
    },
    {
      question: "¿Puedo ajustar el nivel de compresión?",
      answer: "Sí. Puedes elegir entre compresión automática (recomendada, optimiza el balance calidad-tamaño) o ajustar manualmente el nivel de calidad entre 1% y 100%. Un valor entre 70-85% suele dar los mejores resultados para uso web."
    },
    {
      question: "¿Cuánto se reduce el tamaño de los archivos?",
      answer: "Depende del formato y contenido original. Las fotos JPEG típicamente se reducen entre 40-80%. Las imágenes PNG entre 20-50%. En muchos casos puedes pasar de 5MB a menos de 500KB sin diferencia visual notable."
    },
    {
      question: "¿La compresión afecta los metadatos o la orientación?",
      answer: "Preservamos los metadatos esenciales como la orientación EXIF para que las fotos no aparezcan rotadas. Algunos metadatos secundarios (GPS, información de cámara) pueden eliminarse para reducir aún más el tamaño, lo cual también protege tu privacidad."
    },
    {
      question: "¿Funciona en dispositivos móviles?",
      answer: "Sí. CompresorJPEG está optimizado para móviles y tablets. La interfaz se adapta automáticamente a cualquier pantalla. Funciona en Chrome, Safari, Firefox y cualquier navegador moderno."
    },
    {
      question: "¿Cómo comprimo varias imágenes a la vez?",
      answer: "Simplemente arrastra y suelta múltiples archivos al mismo tiempo en la zona de carga, o selecciona varios archivos desde el explorador. Puedes descargar todas las imágenes comprimidas juntas en un archivo ZIP."
    },
    {
      question: "¿Qué diferencia hay entre comprimir JPG y PNG?",
      answer: "JPEG usa compresión con pérdida, lo que reduce mucho el tamaño descartando detalles que el ojo humano apenas percibe — ideal para fotografías. PNG usa compresión sin pérdida, manteniendo cada píxel exacto — ideal para logos, capturas de pantalla e imágenes con texto o transparencia."
    }
  ];

  const faqJsonLd = useMemo(() => {
    if (faqItems.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    };
  }, [faqItems]);

  useEffect(() => {
    if (!faqJsonLd) return;

    document.querySelectorAll('script[id^="faq-structured-data-"]').forEach(el => el.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-structured-data-faq-page';
    script.textContent = JSON.stringify(faqJsonLd);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('faq-structured-data-faq-page');
      if (el) el.remove();
    };
  }, [faqJsonLd]);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Preguntas Frecuentes - Compresor de Imágenes | CompresorJPEG"
        description="Resuelve todas tus dudas sobre compresión de imágenes JPG, PNG, WebP. Guía completa sobre nuestra herramienta gratuita, formatos, calidad y más."
        keywords="preguntas comprimir imagenes, dudas compresor jpg, como comprimir png, ayuda optimizar webp, faq compresion fotos"
        canonical="https://compresorjpeg.com/faq"
      />
      <NavBar />

      <div className="container mx-auto px-4 py-8">
        <BreadcrumbSEO />

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4">Preguntas Frecuentes</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Encuentra respuestas a las preguntas más comunes sobre nuestro compresor de imágenes
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Suspense fallback={null}>
            <AdSlot position="header" className="mb-8" />
          </Suspense>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-center">
                ¿Tienes alguna pregunta?
              </CardTitle>
              <CardDescription className="text-center">
                Aquí encontrarás las respuestas a las preguntas más frecuentes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {faqItems.map((item, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          <Suspense fallback={null}>
            <AdSlot position="inline" className="mt-8" />
          </Suspense>

          <Card className="mt-8">
            <CardHeader className="text-center">
              <CardTitle>¿No encontraste lo que buscabas?</CardTitle>
              <CardDescription>
                Si tienes alguna otra pregunta, no dudes en contactarnos
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-muted-foreground mb-4">
                Estamos aquí para ayudarte con cualquier duda sobre nuestro servicio de compresión de imágenes.
              </p>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Email: soporte@compresorjpeg.com
                </p>
                <p className="text-sm text-muted-foreground">
                  Horario de atención: Lunes a Viernes, 9:00 AM - 6:00 PM (UTC-5)
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default FAQ;
